import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CompaniesService,
  type WebsiteAnalyticsResponse,
  type TCCompany,
  type WebsitePreviewSessionRecord,
  type WebsiteReadResponse,
  type WebsiteDomainCreateRequest,
  type WebsiteDomainRecord,
  type WebsitePublishRequest,
  type WebsiteLogoFinalizeResponse,
  type WebsiteLogoUploadSessionCreate,
  type WebsiteLogoUploadSessionRecord,
  type WebsiteTemplateRegistryListResponse,
  type WebsiteTemplateRegistryRecord,
  type WebsiteTemplateRegistryTemplateSummary,
  type WebsiteTemplateRegistryUpdate,
  type WebsiteTemplateRegistryFinalize,
  type WebsiteTemplateRegistryFinalizeResponse,
  type WebsiteTemplateRegistryRevisionsResponse,
  type WebsiteTemplateRegistryUploadSessionCreate,
  type WebsiteTemplateRegistryUploadSessionRecord,
  type WebsiteTemplateRegistryValidationReport,
  WebsiteTemplateRegistryUploadStatus,
  type WebsiteUpsertRequest,
  WebsitesService,
} from 'src/api/generated';
import type { ApiResponse } from 'src/hooks/api/api';

const STALE_TIME_IN_MS = 30_000;
const NOOP = (): void => undefined;

export const useWebsiteTemplates = (): ApiResponse & {
  templates: WebsiteTemplateRegistryTemplateSummary[];
} => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['websites', 'templates'],
    queryFn: () => WebsitesService.listTemplates(),
    staleTime: STALE_TIME_IN_MS,
  });

  return {
    loading,
    error: error ?? null,
    templates: data ?? [],
    refetch,
  };
};

export const useCurrentWebsite = (): ApiResponse & {
  website: WebsiteReadResponse | null;
} => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['websites', 'current'],
    queryFn: async () => (await WebsitesService.getCurrentWebsite()) ?? null,
    staleTime: STALE_TIME_IN_MS,
  });

  return {
    loading,
    error: error ?? null,
    website: data ?? null,
    refetch,
  };
};

export const useWebsiteAnalytics = (
  days = 30
): ApiResponse & {
  analytics: WebsiteAnalyticsResponse | null;
} => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['websites', 'current', 'analytics', days],
    queryFn: async () =>
      (await WebsitesService.getCurrentWebsiteAnalytics(days)) ?? null,
    staleTime: STALE_TIME_IN_MS,
  });

  return {
    loading,
    error: error ?? null,
    analytics: data ?? null,
    refetch,
  };
};

export const useCurrentCompany = (): ApiResponse & {
  company: TCCompany | null;
} => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['companies', 'current'],
    queryFn: () => CompaniesService.getCurrentCompany(),
    staleTime: STALE_TIME_IN_MS,
  });

  return {
    loading,
    error: error ?? null,
    company: data ?? null,
    refetch,
  };
};

export const useWebsiteTemplateRegistry = (): ApiResponse & {
  registry: WebsiteTemplateRegistryListResponse;
} => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['websites', 'template-registry'],
    queryFn: () => WebsitesService.listTemplateRegistryEntries(),
    staleTime: STALE_TIME_IN_MS,
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (item) =>
          item.latestRevision?.status ===
          WebsiteTemplateRegistryUploadStatus.VALIDATING
      )
        ? 5_000
        : false,
  });

  return {
    loading,
    error: error ?? null,
    registry: data ?? { items: [] },
    refetch,
  };
};

export const useWebsiteTemplateRegistryRevisions = (
  registryTemplateId?: string
): ApiResponse & {
  revisions: WebsiteTemplateRegistryRevisionsResponse | null;
} => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: [
      'websites',
      'template-registry',
      registryTemplateId,
      'revisions',
    ],
    queryFn: () =>
      WebsitesService.listTemplateRegistryRevisions(registryTemplateId ?? ''),
    enabled: Boolean(registryTemplateId),
    staleTime: STALE_TIME_IN_MS,
    refetchInterval: (query) =>
      query.state.data?.revisions.some(
        (revision) =>
          revision.status === WebsiteTemplateRegistryUploadStatus.VALIDATING
      )
        ? 5_000
        : false,
  });

  return {
    loading,
    error: error ?? null,
    revisions: data ?? null,
    refetch,
  };
};

export const useWebsiteTemplateRegistryValidationReport = (
  registryTemplateId?: string,
  revisionId?: string
): ApiResponse & {
  report: WebsiteTemplateRegistryValidationReport | null;
} => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: [
      'websites',
      'template-registry',
      registryTemplateId,
      'revisions',
      revisionId,
      'validation-report',
    ],
    queryFn: () =>
      WebsitesService.getTemplateRegistryValidationReport(
        registryTemplateId ?? '',
        revisionId ?? ''
      ),
    enabled: Boolean(registryTemplateId && revisionId),
    staleTime: STALE_TIME_IN_MS,
  });

  return {
    loading,
    error: error ?? null,
    report: data ?? null,
    refetch,
  };
};

interface MutationOptions<TResponse> {
  onSuccess?: (response: TResponse) => void;
  onError?: (error: Error) => void;
}

export const useUpsertWebsite = (
  options: MutationOptions<WebsiteReadResponse> = {}
) => {
  const queryClient = useQueryClient();
  const { onSuccess = NOOP, onError = NOOP } = options;

  const mutation = useMutation<
    WebsiteReadResponse,
    Error,
    WebsiteUpsertRequest
  >({
    mutationKey: ['websites', 'upsert'],
    mutationFn: (body) => WebsitesService.upsertCurrentWebsite(body),
    onSuccess: async (response) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['websites', 'current'] }),
        queryClient.invalidateQueries({ queryKey: ['websites', 'templates'] }),
      ]);
      onSuccess(response);
    },
    onError,
  });

  return {
    upsertWebsite: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

export const usePublishWebsite = (
  options: MutationOptions<WebsiteReadResponse> = {}
) => {
  const queryClient = useQueryClient();
  const { onSuccess = NOOP, onError = NOOP } = options;

  const mutation = useMutation<
    WebsiteReadResponse,
    Error,
    WebsitePublishRequest
  >({
    mutationKey: ['websites', 'publish'],
    mutationFn: (body) => WebsitesService.publishCurrentWebsite(body),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: ['websites', 'current'],
      });
      onSuccess(response);
    },
    onError,
  });

  return {
    publishWebsite: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

export const useCreateWebsitePreviewSession = (
  options: MutationOptions<WebsitePreviewSessionRecord> = {}
) => {
  const { onSuccess = NOOP, onError = NOOP } = options;

  const mutation = useMutation<
    WebsitePreviewSessionRecord,
    Error,
    WebsiteUpsertRequest
  >({
    mutationKey: ['websites', 'preview-sessions', 'create'],
    mutationFn: (body) => WebsitesService.createPreviewSession(body),
    onSuccess,
    onError,
  });

  return {
    createPreviewSession: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * Creates a mutation that requests an upload session for a managed website logo.
 *
 * The returned mutation only creates the session; callers must upload the file to
 * its upload URL and then finalize the session separately.
 *
 * @param options - Optional callbacks for a successful session or an API error.
 * @returns The asynchronous session creator together with its loading and error state.
 * @throws The returned `createLogoUploadSession` function rejects when validation,
 * authorization, or session creation fails.
 */
export const useCreateWebsiteLogoUploadSession = (
  options: MutationOptions<WebsiteLogoUploadSessionRecord> = {}
) => {
  const { onSuccess = NOOP, onError = NOOP } = options;
  const mutation = useMutation<
    WebsiteLogoUploadSessionRecord,
    Error,
    WebsiteLogoUploadSessionCreate
  >({
    mutationKey: ['websites', 'logo', 'upload-sessions', 'create'],
    mutationFn: (body) => WebsitesService.createLogoUploadSession(body),
    onSuccess,
    onError,
  });

  return {
    createLogoUploadSession: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * Creates a mutation that finalizes a completed website-logo upload session.
 *
 * Finalization verifies the uploaded asset and returns the managed file identifier
 * and durable public URL used by website settings.
 *
 * @param options - Optional callbacks for a finalized logo or an API error.
 * @returns The asynchronous finalizer together with its loading and error state.
 * @throws The returned `finalizeLogoUpload` function rejects when the session is
 * invalid, the uploaded bytes fail validation, or finalization cannot complete.
 */
export const useFinalizeWebsiteLogoUpload = (
  options: MutationOptions<WebsiteLogoFinalizeResponse> = {}
) => {
  const { onSuccess = NOOP, onError = NOOP } = options;
  const mutation = useMutation<WebsiteLogoFinalizeResponse, Error, string>({
    mutationKey: ['websites', 'logo', 'upload-sessions', 'finalize'],
    mutationFn: (uploadSessionId) =>
      WebsitesService.finalizeLogoUpload(uploadSessionId),
    onSuccess,
    onError,
  });

  return {
    finalizeLogoUpload: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

export const useCreateWebsiteTemplateRegistryUploadSession = (
  options: MutationOptions<WebsiteTemplateRegistryUploadSessionRecord> = {}
) => {
  const { onSuccess = NOOP, onError = NOOP } = options;

  const mutation = useMutation<
    WebsiteTemplateRegistryUploadSessionRecord,
    Error,
    WebsiteTemplateRegistryUploadSessionCreate
  >({
    mutationKey: ['websites', 'template-registry', 'upload-sessions', 'create'],
    mutationFn: (body) =>
      WebsitesService.createTemplateRegistryUploadSession(body),
    onSuccess,
    onError,
  });

  return {
    createUploadSession: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

export const useFinalizeWebsiteTemplateRegistryUpload = (
  options: MutationOptions<WebsiteTemplateRegistryFinalizeResponse> = {}
) => {
  const queryClient = useQueryClient();
  const { onSuccess = NOOP, onError = NOOP } = options;

  const mutation = useMutation<
    WebsiteTemplateRegistryFinalizeResponse,
    Error,
    WebsiteTemplateRegistryFinalize
  >({
    mutationKey: ['websites', 'template-registry', 'finalize'],
    mutationFn: (body) => WebsitesService.finalizeTemplateRegistryUpload(body),
    onSuccess: async (response) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['websites', 'template-registry'],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'websites',
            'template-registry',
            response.registryTemplateId,
            'revisions',
          ],
        }),
      ]);
      onSuccess(response);
    },
    onError,
  });

  return {
    finalizeUpload: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

export const useUpdateWebsiteTemplateRegistryEntry = (
  options: MutationOptions<WebsiteTemplateRegistryRecord> = {}
) => {
  const queryClient = useQueryClient();
  const { onSuccess = NOOP, onError = NOOP } = options;

  const mutation = useMutation<
    WebsiteTemplateRegistryRecord,
    Error,
    { registryTemplateId: string; body: WebsiteTemplateRegistryUpdate }
  >({
    mutationKey: ['websites', 'template-registry', 'update'],
    mutationFn: ({ registryTemplateId, body }) =>
      WebsitesService.updateTemplateRegistryEntry(registryTemplateId, body),
    onSuccess: async (response) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['websites', 'template-registry'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['websites', 'templates'],
        }),
      ]);
      onSuccess(response);
    },
    onError,
  });

  return {
    updateTemplate: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

export const useCreateWebsiteDomain = (
  options: MutationOptions<WebsiteDomainRecord> = {}
) => {
  const queryClient = useQueryClient();
  const { onSuccess = NOOP, onError = NOOP } = options;

  const mutation = useMutation({
    mutationKey: ['websites', 'domains', 'create'],
    mutationFn: (body: WebsiteDomainCreateRequest) =>
      WebsitesService.createDomain(body),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: ['websites', 'current'],
      });
      onSuccess(response);
    },
    onError,
  });

  return {
    createDomain: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

export const useVerifyWebsiteDomain = (
  options: MutationOptions<WebsiteReadResponse> = {}
) => {
  const queryClient = useQueryClient();
  const { onSuccess = NOOP, onError = NOOP } = options;

  const mutation = useMutation<WebsiteReadResponse, Error, number>({
    mutationKey: ['websites', 'domains', 'verify'],
    mutationFn: (domainId) => WebsitesService.verifyDomain(domainId),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: ['websites', 'current'],
      });
      onSuccess(response);
    },
    onError,
  });

  return {
    verifyDomain: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
